import React, { useState, useRef, useCallback, useId, useEffect, useLayoutEffect } from 'react';
import './Tabs.css';

interface IndicatorStyle {
  left: number;
  width: number;
  height: number;
}

export interface TabItem {
  /** Unique identifier for the tab */
  id: string;
  /** Tab button label */
  label: React.ReactNode;
  /** Tab panel content */
  content: React.ReactNode;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Optional icon to display before the label */
  icon?: React.ReactNode;
}

export interface TabsProps {
  /** Array of tab definitions */
  tabs: TabItem[];
  /** Controlled: currently active tab ID */
  activeTab?: string;
  /** Uncontrolled: initial tab ID (defaults to first non-disabled tab) */
  defaultTab?: string;
  /** Called when active tab changes */
  onTabChange?: (tabId: string) => void;
  /** Visual style variant */
  variant?: 'default' | 'pills' | 'underline';
  /** Custom class for container */
  className?: string;
  /** Accessible label for the tablist */
  'aria-label'?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * Tabs component for switching between content panels.
 * Supports both controlled and uncontrolled modes.
 */
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  defaultTab,
  onTabChange,
  variant = 'default',
  className = '',
  'aria-label': ariaLabel,
  style,
}) => {
  const generatedId = useId();
  const baseId = `hi-ui-tabs-${generatedId}`;
  const tablistRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Indicator position for animated underline/pill
  const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle>({
    left: 0,
    width: 0,
    height: 0,
  });
  const [indicatorReady, setIndicatorReady] = useState(false);

  // Find first non-disabled tab for default
  const getFirstEnabledTab = useCallback(() => {
    const firstEnabled = tabs.find((tab) => !tab.disabled);
    return firstEnabled?.id ?? tabs[0]?.id;
  }, [tabs]);

  // Uncontrolled state
  const [internalTab, setInternalTab] = useState(() => defaultTab ?? getFirstEnabledTab());

  // Determine if controlled
  const isControlled = activeTab !== undefined;
  const currentTab = isControlled ? activeTab : internalTab;

  const handleTabChange = useCallback(
    (id: string) => {
      if (!isControlled) {
        setInternalTab(id);
      }
      onTabChange?.(id);
    },
    [isControlled, onTabChange]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const enabledTabs = tabs.filter((tab) => !tab.disabled);
      const currentIndex = enabledTabs.findIndex((tab) => tab.id === currentTab);

      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = enabledTabs.length - 1;
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextIndex = currentIndex + 1;
          if (nextIndex >= enabledTabs.length) nextIndex = 0;
          break;
        case 'Home':
          event.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          nextIndex = enabledTabs.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex !== null && enabledTabs[nextIndex]) {
        const nextTab = enabledTabs[nextIndex];
        handleTabChange(nextTab.id);
        // Focus the tab button
        const tabButton = tablistRef.current?.querySelector(
          `[data-tab-id="${nextTab.id}"]`
        ) as HTMLButtonElement | null;
        tabButton?.focus();
      }
    },
    [tabs, currentTab, handleTabChange]
  );

  // Update indicator position when active tab changes
  const updateIndicator = useCallback(() => {
    if (!currentTab) return;

    const activeButton = tabRefs.current.get(currentTab);
    const tablist = tablistRef.current;

    if (activeButton && tablist) {
      const tablistRect = tablist.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      setIndicatorStyle({
        left: buttonRect.left - tablistRect.left + tablist.scrollLeft,
        width: buttonRect.width,
        height: buttonRect.height,
      });
      setIndicatorReady(true);
    }
  }, [currentTab]);

  // Update indicator position before paint
  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  // Also update on window resize
  useEffect(() => {
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  // Handle empty tabs array
  if (!tabs.length) {
    return null;
  }

  const containerClasses = ['hi-ui-tabs', `hi-ui-tabs--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  const currentTabData = tabs.find((tab) => tab.id === currentTab);

  return (
    <div className={containerClasses} style={style} data-testid="tabs">
      <div
        ref={tablistRef}
        className="hi-ui-tabs__list"
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === currentTab;
          const tabClasses = [
            'hi-ui-tabs__tab',
            isActive && 'hi-ui-tabs__tab--active',
            tab.disabled && 'hi-ui-tabs__tab--disabled',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabRefs.current.set(tab.id, el);
                } else {
                  tabRefs.current.delete(tab.id);
                }
              }}
              id={`${baseId}-tab-${tab.id}`}
              className={tabClasses}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${baseId}-panel-${tab.id}`}
              aria-disabled={tab.disabled}
              tabIndex={isActive ? 0 : -1}
              data-tab-id={tab.id}
              onClick={() => handleTabChange(tab.id)}
              disabled={tab.disabled}
              type="button"
            >
              {tab.icon && <span className="hi-ui-tabs__tab-icon">{tab.icon}</span>}
              <span className="hi-ui-tabs__tab-label">{tab.label}</span>
            </button>
          );
        })}
        {indicatorReady && (
          <span
            className="hi-ui-tabs__indicator"
            style={{
              transform: `translateX(${indicatorStyle.left}px)`,
              width: `${indicatorStyle.width}px`,
              ...(variant === 'pills' ? { height: `${indicatorStyle.height}px` } : {}),
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {currentTabData && (
        <div
          key={currentTabData.id}
          id={`${baseId}-panel-${currentTabData.id}`}
          className="hi-ui-tabs__panel"
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${currentTabData.id}`}
          tabIndex={0}
        >
          {currentTabData.content}
        </div>
      )}
    </div>
  );
};
